import {useRef, useCallback} from 'react'

const useLayoutUtilities = (
  {showFullScreen},
  {setMaxWidth, setMinWidth, setContainerWidth},
  {getMinWidth, getMaxWidth}
) => {
  const scrollTimeoutRef = useRef(null)
  const autoScrollTimeoutRef = useRef(null)
  const autoScrollToMessageTimeoutRef = useRef(null)
  const scrollTopTimeoutRef = useRef(null)

  const resetSizes = useCallback(() => {
    setMaxWidth(getMaxWidth())
    setMinWidth(getMinWidth())
    if (showFullScreen) {
      setContainerWidth(getMaxWidth())
    }
  }, [showFullScreen, setMaxWidth, setMinWidth, setContainerWidth, getMaxWidth, getMinWidth])

  const adjustChatBodyMaxHeight = useCallback((chatBodyScrollContainerRef, chatFooterContainerRef) => {
    if (chatBodyScrollContainerRef.current && chatFooterContainerRef.current) {
      chatBodyScrollContainerRef.current.style.maxHeight = `calc(100vh - ${
        chatFooterContainerRef.current.clientHeight + 88
      }px)`
    }
  }, [])

  const debounce = useCallback((func, wait) => {
    let timeout
    return (...args) => {
      clearTimeout(timeout)
      timeout = setTimeout(() => func.apply(this, args), wait)
    }
  }, [])

  const handleScroll = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current)
    }
    scrollTimeoutRef.current = setTimeout(() => {}, 200)
  }, [])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedHandleScroll = useCallback(debounce(handleScroll, 200), [debounce, handleScroll])

  const clearAllTimeouts = useCallback(() => {
    if (autoScrollTimeoutRef.current) {
      clearTimeout(autoScrollTimeoutRef.current)
    }
    if (autoScrollToMessageTimeoutRef.current) {
      clearTimeout(autoScrollToMessageTimeoutRef.current)
    }
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current)
    }
    if (scrollTopTimeoutRef.current) {
      clearTimeout(scrollTopTimeoutRef.current)
    }
  }, [])

  return {
    resetSizes,
    adjustChatBodyMaxHeight,
    debounce,
    handleScroll,
    debouncedHandleScroll,
    clearAllTimeouts,
    scrollTimeoutRef,
    autoScrollTimeoutRef,
    autoScrollToMessageTimeoutRef,
    scrollTopTimeoutRef
  }
}

export default useLayoutUtilities

/*
CHATBOT LAYOUT UTILITIES HOOK

KEY FUNCTIONS:
- resetSizes: Adjusts chat window dimensions based on screen constraints
- adjustChatBodyMaxHeight: Dynamically sets max height based on footer size
- debounce: Utility function for limiting function execution frequency
- handleScroll: Manages scroll events with timeout handling
- clearAllTimeouts: Cleanup utility to prevent memory leaks

FEATURES:
- Responsive layout management
- Scroll behavior optimization
- Timeout reference management
- Debounced scroll handling for performance
- Memory leak prevention through cleanup
*/
